import { FC, useEffect, useRef, useState } from "react";
import clsx from "classnames";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { OtpInput } from "../OtpInput";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { isTV } from "../../../utils/platform";
import { useStyles } from "./PinCodeDialog.style";

const toDigits = (value: string, length: number) =>
  Array.from({ length }, (_, index) => value[index] ?? "");

export interface IPinCodeResend {
  prompt: string;
  actionText: string;
  onClick?: () => void;
}

export interface IPinCodeLink {
  text: string;
  gamepadId: string;
  onClick?: () => void;
}

interface IPinCodeDialogProps {
  open: boolean;
  title: string;
  description: string;
  length: number;
  value: string;
  errorMessage?: string;
  links?: IPinCodeLink[];
  resend?: IPinCodeResend;
  submitText: string;
  cancelText: string;
  isMasked?: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const PinCodeDialog: FC<IPinCodeDialogProps> = ({
  open,
  title,
  description,
  length,
  value,
  errorMessage,
  links = [],
  resend,
  submitText,
  cancelText,
  isMasked,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const classes = useStyles();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Boxes can be edited out of order, so the gaps live here; the parent gets the digits joined
  const [digits, setDigits] = useState(() => toDigits(value, length));

  useEffect(() => {
    if (value !== digits.join("")) setDigits(toDigits(value, length));
  }, [value, length]);

  // Opens ready to type: on TV the first box brings up the keyboard
  useEffect(() => {
    if (!open) return;
    const firstBox = dialogRef.current?.querySelector<HTMLElement>(
      '[data-gamepad="pin-code-digit-0"]',
    );
    if (isTV) {
      firstBox?.click();
    } else {
      firstBox?.querySelector<HTMLInputElement>("input")?.focus();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      data-dialog-nav="rows"
      dialogRef={dialogRef}
      title={title}
      onClose={onCancel}
      mainAction={{
        text: submitText,
        onClick: onSubmit,
        disabled: value.length < length,
        "data-gamepad": "pin-code-submit",
      }}
      secondAction={{
        text: cancelText,
        onClick: onCancel,
        "data-gamepad": "pin-code-cancel",
      }}
    >
      <div className={classes.content}>
        <div className={classes.header}>
          <Typography
            variant={TypographyVariant.HeadingH5Bold}
            className={classes.title}
          >
            {title}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB6Medium}
            color={TypographyColor.Silver}
            className={classes.description}
          >
            {description}
          </Typography>
        </div>

        <div className={classes.field}>
          <OtpInput
            length={length}
            digits={digits}
            onChange={(next) => {
              setDigits(next);
              onChange(next.join(""));
            }}
            onEnter={() => {
              if (value.length >= length) onSubmit();
            }}
            gamepadPrefix="pin-code-digit"
            ariaLabel={title}
            isError={!!errorMessage}
            isMasked={isMasked}
            keyboardPortalRef={dialogRef}
          >
            {errorMessage && (
              <Typography
                role="alert"
                variant={TypographyVariant.BodyB6Regular}
                color={TypographyColor.Error}
                className={classes.error}
              >
                <span
                  className={clsx("icon-bold-info-circle", classes.errorIcon)}
                />
                {errorMessage}
              </Typography>
            )}
          </OtpInput>
        </div>

        {resend && (
          <Typography
            variant={TypographyVariant.BodyB6Regular}
            color={TypographyColor.Silver}
            className={clsx(classes.resend, {
              [classes.resendActive]: !!resend.onClick,
            })}
            onClick={resend.onClick}
            data-gamepad={resend.onClick ? "pin-code-resend" : undefined}
            role="button"
            tabIndex={-1}
            aria-disabled={!resend.onClick || undefined}
            data-selected-class={classes.selected}
          >
            {resend.prompt}{" "}
            <span
              className={
                resend.onClick ? classes.resendLink : classes.resendCountdown
              }
            >
              {resend.actionText}
            </span>
          </Typography>
        )}

        {links.length > 0 && (
          <div className={clsx(classes.links, { single: links.length === 1 })}>
            {links.map((link) => (
              <Typography
                key={link.gamepadId}
                variant={TypographyVariant.BodyB6Bold}
                className={classes.link}
                onClick={link.onClick}
                data-gamepad={link.gamepadId}
                role="button"
                tabIndex={-1}
                data-selected-class={classes.selected}
              >
                {link.text}
              </Typography>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};
